/**
 * markdown-it inline rule turning `<span color="#00ff00">text</span>` into a
 * paired `span_color_open` / `span_color_close` token (react-native-markdown-display
 * collapses the pair into a single nested `span_color` AST node with a `color`
 * attribute). The inner text is tokenized as regular inline markdown, so
 * nesting like `<span color="...">**bold**</span>` works.
 *
 * Types are declared locally because markdown-it v10 (the version bundled with
 * react-native-markdown-display) ships no TypeScript definitions.
 */

type InlineToken = {
  attrSet: (name: string, value: string) => void
}

type InlineState = {
  src: string
  pos: number
  posMax: number
  md: { inline: { tokenize: (state: InlineState) => void } }
  push: (type: string, tag: string, nesting: -1 | 0 | 1) => InlineToken
}

type MarkdownItLike = {
  inline: {
    ruler: {
      before: (
        beforeName: string,
        ruleName: string,
        rule: (state: InlineState, silent: boolean) => boolean
      ) => void
    }
  }
}

const OPEN_TAG = /^<span\s+color="([^"]+)"\s*>/i
const CLOSE_TAG = '</span>'

export default function spanColorPlugin(md: MarkdownItLike) {
  md.inline.ruler.before('html_inline', 'span_color', (state, silent) => {
    const { src, pos, posMax } = state
    if (src.charCodeAt(pos) !== 0x3c /* < */) {
      return false
    }
    const open = OPEN_TAG.exec(src.slice(pos, posMax))
    if (!open) {
      return false
    }
    const contentStart = pos + open[0].length
    const closeStart = src.indexOf(CLOSE_TAG, contentStart)
    if (closeStart < 0 || closeStart + CLOSE_TAG.length > posMax) {
      return false
    }

    if (!silent) {
      state.push('span_color_open', 'span', 1).attrSet('color', open[1])
      state.pos = contentStart
      state.posMax = closeStart
      state.md.inline.tokenize(state)
      state.posMax = posMax
      state.push('span_color_close', 'span', -1)
    }

    state.pos = closeStart + CLOSE_TAG.length
    return true
  })
}
